import { ReactNode, useRef, useState } from 'react';
import { IconType } from 'react-icons';
import {
  FiCheckCircle,
  FiClock,
  FiCreditCard,
  FiFileText,
  FiPackage,
  FiSliders,
  FiTag,
  FiTruck,
  FiUser,
} from 'react-icons/fi';
import OrderApproval from '../../../../../components/OrderApproval';
import { Order, OrderItem } from '../../../../../types/apiResponse/order';
import { Label, Translate } from './messages';
import { erpStatus, flag, number, orderStatus, reference, text, validDate } from './presentation';
import * as s from './styles';
import { useOrderMoneyDecimals } from '../../OrderMoneyPrecision';
import { formatOrderMoney } from '../../orderMoney';
import { orderFinancials } from './financials';
import OrderItems from './OrderItems';
import OrderFinancialSummary from './OrderFinancialSummary';

export type OrderDetailsTab =
  | 'summary'
  | 'general'
  | 'customer'
  | 'seller'
  | 'commercial'
  | 'delivery'
  | 'items'
  | 'approval';
type Tab = OrderDetailsTab;
const tabs: { id: Tab; icon: IconType; label: Label }[] = [
  { id: 'summary', icon: FiFileText, label: 'order' },
  { id: 'general', icon: FiSliders, label: 'general' },
  { id: 'customer', icon: FiUser, label: 'customer' },
  { id: 'seller', icon: FiUser, label: 'seller' },
  { id: 'commercial', icon: FiCreditCard, label: 'payment' },
  { id: 'delivery', icon: FiTruck, label: 'delivery' },
  { id: 'items', icon: FiPackage, label: 'items' },
  { id: 'approval', icon: FiCheckCircle, label: 'approval' },
];

function Section({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon: IconType;
  children: ReactNode;
}) {
  return (
    <s.Card>
      <h3>
        <Icon size={18} aria-hidden="true" />
        {title}
      </h3>
      {children}
    </s.Card>
  );
}

function Detail({ label, value }: { label: string; value: ReactNode }) {
  return (
    <s.Field>
      <dt>{label}</dt>
      <dd title={typeof value === 'string' ? value : undefined}>{value}</dd>
    </s.Field>
  );
}

export default function OrderDetailsContent({
  order,
  l,
  locale,
  initialTab = 'summary',
  onPendenciesChanged,
}: {
  order: Order;
  l: Translate;
  locale: string;
  initialTab?: OrderDetailsTab;
  onPendenciesChanged?: () => void;
}) {
  const [tab, setTab] = useState<Tab>(initialTab);
  const contentRef = useRef<HTMLDivElement>(null);
  const decimals = useOrderMoneyDecimals();
  const financial = orderFinancials(order, decimals);
  const display = (value: unknown) => text(value) || l('unknown');
  const decimal = (value: unknown) => {
    const parsed = number(value);
    return parsed == null
      ? l('unknown')
      : new Intl.NumberFormat(locale, { maximumFractionDigits: 4 }).format(parsed);
  };
  const money = (value: unknown) => formatOrderMoney(value, decimals, locale, l('unknown'));
  const percentage = (value: unknown) =>
    number(value) === null ? l('unknown') : `${decimal(value)}%`;
  const date = (value: unknown) => {
    const raw = validDate(value);
    if (!raw) return l('unknown');
    // Keep dates without a timezone in the recorded calendar day (no UTC day shift).
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      const [year, month, day] = raw.split('-').map(Number);
      const parsed = new Date(year, month - 1, day);
      if (
        parsed.getFullYear() !== year ||
        parsed.getMonth() !== month - 1 ||
        parsed.getDate() !== day
      )
        return raw;
      return new Intl.DateTimeFormat(locale).format(parsed);
    }
    const parsed = new Date(raw.replace(' ', 'T'));
    if (Number.isNaN(parsed.getTime())) return raw;
    return new Intl.DateTimeFormat(locale, { dateStyle: 'short', timeStyle: 'short' }).format(
      parsed,
    );
  };
  const boolean = (value: unknown) => {
    if (flag(value) === 's') return l('yes');
    if (flag(value) === 'n') return l('no');
    return display(value);
  };
  const check = (value: unknown) => {
    if (flag(value) === 's')
      return (
        <s.Badge $tone="positive">
          <FiCheckCircle aria-hidden="true" />
          {l('checkOk')}
        </s.Badge>
      );
    if (flag(value) === 'n')
      return (
        <s.Badge $tone="warning">
          <FiClock aria-hidden="true" />
          {l('checkPending')}
        </s.Badge>
      );
    return <s.Badge>{display(value)}</s.Badge>;
  };
  const detail = (label: Label, value: ReactNode) => (
    <Detail key={label} label={l(label)} value={value} />
  );
  const note = (label: Label, value: unknown) =>
    text(value) ? (
      <s.Note key={label}>
        <strong>{l(label)}</strong>
        <p>{text(value)}</p>
      </s.Note>
    ) : null;
  const status = orderStatus(order);
  const integration = erpStatus(order);
  const erpError = order.erp_info?.ExportadoErpErro ?? order.ExportadoErpErro;
  const orderType = reference(order.order_type?.DescricaoTipoPed, order.CodigoTipoPedido);
  const items = order.items;
  const notes = (
    [
      ['note1', order.Obs1],
      ['note2', order.Obs2],
      ['complement1', order.Complemento1],
      ['complement2', order.Complemento2],
    ] as [Label, unknown][]
  ).filter(([, value]) => text(value));

  const customerSection = (
    <Section title={l('customer')} icon={FiUser}>
      <s.CustomerName>{display(order.customer?.RazaoSocial || order.RazaoSocial)}</s.CustomerName>
      <dl>
        {detail('document', display(order.customer?.CGCCPF))}
        {detail('customerCode', display(order.CodigoCliente))}
        {detail('company', display(order.CodigoEmpresa))}
      </dl>
    </Section>
  );
  const identification = (
    <Section title={l('identification')} icon={FiFileText}>
      <dl>
        {detail('deviceNumber', display(order.NumeroPedidoDisp))}
        {detail('smartsellNumber', display(order.NumeroPedido))}
        {detail('erpNumber', display(order.erp_info?.ErpNumeroPedido || order.ErpNumeroPedido))}
        {detail('purchaseOrder', display(order.OrdemCompra))}
      </dl>
    </Section>
  );
  const sellerSection = (
    <Section title={l('seller')} icon={FiUser}>
      <s.CustomerName>{display(order.seller?.Nome)}</s.CustomerName>
      <dl>
        {detail('customerCode', display(order.CodigoVendedor))}
        {detail('company', display(order.seller?.CodigoFilial || order.CodigoEmpresa))}
        {detail('phone', display(order.seller?.Telefone))}
        {detail('email', display(order.seller?.Email))}
      </dl>
    </Section>
  );
  const summaryCommercial = (
    <Section title={l('commercialConditions')} icon={FiCreditCard}>
      <dl>
        {detail('orderType', display(order.order_type?.DescricaoTipoPed || order.CodigoTipoPedido))}
        {detail('budget', boolean(order.EOrcamento))}
        {detail(
          'paymentMethod',
          display(order.payment_method?.Descricao || order.CodigoFormaPagto),
        )}
        {detail(
          'paymentCondition',
          display(order.paymentcondition?.Descricao || order.CodigoCondPagto),
        )}
        {detail('priceTable', display(order.CodigoTabPreco))}
      </dl>
    </Section>
  );
  const summaryDelivery = (
    <Section title={l('deliveryBilling')} icon={FiTruck}>
      <dl>
        {detail(
          'billingUnit',
          display(order.billing_unit?.DescricaoUnidFat || order.CodigoUnidFat),
        )}
        {detail('freightType', display(order.TipoFretePadrao))}
      </dl>
    </Section>
  );
  const registeredValues = (
    <Section title={l('values')} icon={FiCreditCard}>
      <s.Hint>{l('valuesHint')}</s.Hint>
      <dl>
        {detail('freightValue', money(order.valor_do_frete))}
        {detail('unloadingValue', money(order.valor_de_descarga))}
        {detail('commission', money(order.ValorComissao))}
        {detail('profitability', decimal(order.RentabilidadePedido))}
      </dl>
    </Section>
  );
  const payment = (
    <Section title={l('payment')} icon={FiCreditCard}>
      <dl>
        {detail(
          'paymentMethod',
          display(reference(order.payment_method?.Descricao, order.CodigoFormaPagto)),
        )}
        {detail(
          'paymentCondition',
          display(reference(order.paymentcondition?.Descricao, order.CodigoCondPagto)),
        )}
        {detail('priceTable', display(order.CodigoTabPreco))}
        {detail('promotion', display(order.CodigoPromocao))}
        {detail('discountGeneral', percentage(order.Desconto1))}
        {detail('discount2', percentage(order.Desconto2))}
        {detail('partial', boolean(order.aceita_pedido_parcial))}
      </dl>
    </Section>
  );
  const observations = (
    <Section title={l('notes')} icon={FiFileText}>
      {notes.length ? (
        notes.map(([label, value]) => note(label, value))
      ) : (
        <s.Hint>{l('noNotes')}</s.Hint>
      )}
    </Section>
  );

  const itemDetails = (item: OrderItem) => (
    <div className="item-detail">
      <dl>
        {detail('recordedSaleValue', money(item.ValorVenda))}
        {detail('gross', money(item.ValorBruto))}
        {detail('baseUnitPrice', money(item.Preco))}
        {detail('tablePrice', money(item.preco_tabela))}
        {detail('discount1', percentage(item.Desconto1))}
        {detail('discount2', percentage(item.Desconto2))}
        {detail('discount3', percentage(item.Desconto3))}
        {detail('surcharge', decimal(item.Acrescimo))}
        {detail('itemFreight', money(item.VlrFrete))}
        {detail('tax', money(item.Imposto_St))}
        {detail('bonus', decimal(item.QtdBonif))}
        {detail('balance', decimal(item.SaldoItem))}
        {detail('priceTable', display(item.CodigoTabPreco))}
        {detail('promotion', display(item.CodigoPromocao))}
        {detail('cfop', display(item.NumeroCfop))}
        {detail('multiple', decimal(item.multiplo))}
        {detail('priceApproval', check(item.aprov_preco))}
      </dl>
      {note('priceApproval', item.aprov_preco_obs)}
      {note('complement1', item.Complemento1)}
      {note('complement2', item.Complemento2)}
    </div>
  );

  const metrics = (
    <s.Metrics aria-label={l('orderMetrics')}>
      {(
        [
          ['creditLimit', money(order.customer?.credit_limit?.LimiteCredito)],
          ['items', decimal(financial.count)],
          ['grossShort', money(financial.gross)],
          ['netShort', money(financial.net)],
        ] as [Label, string][]
      ).map(([label, value]) => (
        <div key={label}>
          <dt>{l(label)}</dt>
          <dd title={typeof value === 'string' ? value : undefined}>{value}</dd>
        </div>
      ))}
    </s.Metrics>
  );
  const itemsSection = (compact = false) => (
    <Section title={l('orderItems')} icon={FiPackage}>
      <OrderItems
        items={items}
        decimals={decimals}
        l={l}
        money={money}
        decimal={decimal}
        itemDetails={itemDetails}
        compact={compact}
      />
    </Section>
  );
  const logistics = (
    <Section title={l('logistics')} icon={FiTruck}>
      <dl>
        {detail(
          'billingUnit',
          display(reference(order.billing_unit?.DescricaoUnidFat, order.CodigoUnidFat)),
        )}
        {detail('state', display(order.billing_unit?.UF))}
        {detail('carrier', display(order.CodigoTransportador))}
        {detail('address', display(order.CodigoEnderecoEntrega || order.pedido_codigo_endereco))}
        {detail('destination', display(order.codigoClienteDestinatario))}
        {detail('freightType', display(order.TipoFretePadrao))}
        {detail('deliveryForm', display(order.FormaEntrega))}
        {detail('freightName', display(order.CodigoNomeFrete))}
        {detail('weight', decimal(order.PesoPedido))}
        {detail('freightValue', money(order.valor_do_frete))}
        {detail('unloadingValue', money(order.valor_de_descarga))}
      </dl>
    </Section>
  );

  const changeTab = (next: Tab) => {
    setTab(next);
    contentRef.current?.scrollTo({ top: 0 });
  };

  return (
    <s.Shell>
      <s.Tabs role="tablist" aria-label={l('navigation')}>
        {tabs.map(({ id, icon: Icon, label }, index) => (
          <button
            key={id}
            type="button"
            role="tab"
            id={`order-detail-tab-${id}`}
            aria-controls={`order-detail-panel-${id}`}
            aria-selected={tab === id}
            tabIndex={tab === id ? 0 : -1}
            onClick={() => changeTab(id)}
            onKeyDown={(event) => {
              let nextIndex: number | undefined;
              if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
              if (event.key === 'ArrowLeft') nextIndex = (index + tabs.length - 1) % tabs.length;
              if (event.key === 'Home') nextIndex = 0;
              if (event.key === 'End') nextIndex = tabs.length - 1;
              if (nextIndex === undefined) return;
              event.preventDefault();
              changeTab(tabs[nextIndex].id);
              const button = event.currentTarget.parentElement?.children[nextIndex] as
                | HTMLButtonElement
                | undefined;
              button?.focus();
            }}
          >
            <Icon aria-hidden="true" />
            {l(label)}
          </button>
        ))}
      </s.Tabs>
      <s.Content
        ref={contentRef}
        role="tabpanel"
        id={`order-detail-panel-${tab}`}
        aria-labelledby={`order-detail-tab-${tab}`}
        tabIndex={0}
      >
        {tab === 'approval' && (
          <OrderApproval
            orderNumber={order.NumeroPedido}
            exportedErp={order.ExportadoErp}
            onChange={onPendenciesChanged}
          />
        )}
        {tab === 'summary' && (
          <>
            <s.Hero>
              <div className="hero-heading">
                <span className="hero-icon">
                  <FiFileText size={25} aria-hidden="true" />
                </span>
                <div>
                  <h2>{l('summaryTitle')}</h2>
                  <p>{l('summaryDescription')}</p>
                </div>
              </div>
              <div className="hero-meta">
                <div>
                  <span>{l('createdAt')}</span>
                  <strong>{date(order.created_at || order.DataPedido)}</strong>
                </div>
                <div>
                  <span>{l('updatedAt')}</span>
                  <strong>{date(order.updated_at)}</strong>
                </div>
              </div>
            </s.Hero>
            <s.Grid>
              {identification}
              {customerSection}
            </s.Grid>
            <s.Grid>
              {summaryCommercial}
              {summaryDelivery}
            </s.Grid>
            {itemsSection(true)}
            <OrderFinancialSummary totals={financial} l={l} money={money} decimal={decimal} />
          </>
        )}
        {tab === 'general' && (
          <>
            <s.StatusRow>
              <s.Badge>{l(status)}</s.Badge>
              <s.Badge>{l(integration)}</s.Badge>
            </s.StatusRow>
            {text(erpError) && (
              <Section title={l('integration')} icon={FiTruck}>
                {note('integrationMessage', erpError)}
              </Section>
            )}
            {identification}
            <Section title={l('general')} icon={FiSliders}>
              <dl>
                {detail('orderType', display(orderType))}
                {detail('budget', boolean(order.EOrcamento))}
                {detail('purchaseOrder', display(order.OrdemCompra))}
              </dl>
            </Section>
            {observations}
          </>
        )}
        {tab === 'customer' && customerSection}
        {tab === 'seller' && sellerSection}
        {tab === 'commercial' && (
          <>
            <s.Grid>
              {payment}
              {registeredValues}
            </s.Grid>
            {observations}
          </>
        )}
        {tab === 'items' && (
          <s.ItemsPanel>
            <h2>{l('items')}</h2>
            <s.PriceTable>
              <span className="price-icon">
                <FiTag size={24} aria-hidden="true" />
              </span>
              <div>
                <span>{l('priceTable')}</span>
                <strong>{display(order.CodigoTabPreco)}</strong>
                <small>
                  {decimal(financial.count)} {l('productsCount')}
                </small>
              </div>
            </s.PriceTable>
            <s.GeneralDiscount>
              <label>
                {l('generalDiscountInput')}
                <input type="number" value={financial.generalPercent} disabled />
              </label>
            </s.GeneralDiscount>
            {metrics}
            <OrderItems
              items={items}
              decimals={decimals}
              l={l}
              money={money}
              decimal={decimal}
              itemDetails={itemDetails}
            />
          </s.ItemsPanel>
        )}
        {tab === 'delivery' && (
          <s.Grid>
            {logistics}
            <Section title={l('milestones')} icon={FiClock}>
              <dl>
                {detail('deliveryDate', date(order.DataEntrega))}
                {detail('billingDate', date(order.DataFaturamento))}
                {detail('emission', date(order.DataEmissao))}
                {detail('cutoff', date(order.DataCorte))}
                {detail('load', date(order.DataCarga))}
                {detail('departure', date(order.DataSaida))}
              </dl>
            </Section>
          </s.Grid>
        )}
      </s.Content>
    </s.Shell>
  );
}
